import {useMemo, useCallback} from 'react';
import {LayerId} from '@mc/react-mapio';
import {LayerAttributeVO, LayerVO} from '../api';
import {getLayerConfig, getParentLayerId, isParentOfUserDbLayers} from '../helpers/LayerConfigUtils';
import {UseQueryResult} from 'react-query';

export type DiscriminatorResult = {
    discriminatorAttribute: LayerAttributeVO | undefined;
    matchingCiselnik: LayerAttributeVO['ciselniky'][number] | undefined;
};

/**
 * Hook pro výpočet diskriminačních dat vrstvy.
 * Detekuje diskriminační atribut a odpovídající číselník na základě parent/child vztahů.
 */
export const useDiscriminatorData = (
    selectedLayerId: LayerId,
    attributeTableDiscriminatedChildId: LayerId | null,
    layerAttributesQuery: UseQueryResult<LayerAttributeVO[]>,
    layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}>,
    userDbLayers: LayerId[],
): DiscriminatorResult => {
    const isParentOfUserDbLayersLocal = useCallback(
        (layerId: LayerId): boolean => isParentOfUserDbLayers(layersConfigQuery.data, userDbLayers, layerId),
        [layersConfigQuery.data, userDbLayers],
    );

    return useMemo(() => {
        if (!layerAttributesQuery.data || !layersConfigQuery.data) {
            return {discriminatorAttribute: undefined, matchingCiselnik: undefined};
        }

        if (attributeTableDiscriminatedChildId) {
            const childLayerConfig = getLayerConfig(layersConfigQuery.data, attributeTableDiscriminatedChildId);
            const layerLabel = childLayerConfig?.label;
            if (!layerLabel) return {discriminatorAttribute: undefined, matchingCiselnik: undefined};

            const discriminatorAttribute = layerAttributesQuery.data.find(a => a.diskriminator && a.ciselniky?.some(c => c.nazev === layerLabel));
            const matchingCiselnik = discriminatorAttribute?.ciselniky?.find(c => c.nazev === layerLabel);

            if (discriminatorAttribute && matchingCiselnik) {
                discriminatorAttribute.layerId = String(attributeTableDiscriminatedChildId);
            }

            return {
                discriminatorAttribute: discriminatorAttribute ?? undefined,
                matchingCiselnik: matchingCiselnik ?? undefined,
            };
        }

        const isParentLayer = isParentOfUserDbLayersLocal(selectedLayerId);
        if (!isParentLayer) return {discriminatorAttribute: undefined, matchingCiselnik: undefined};

        const childUserDbLayers = userDbLayers.filter(childLayerId => {
            const pId = getParentLayerId(layersConfigQuery.data, childLayerId);
            return pId === selectedLayerId;
        });

        const discriminatorAttribute = layerAttributesQuery.data.find(attribute => {
            if (!attribute.diskriminator || !attribute.ciselniky) return false;
            return childUserDbLayers.every(childLayerId => {
                const childLayerConfig = getLayerConfig(layersConfigQuery.data, childLayerId);
                const layerLabel = childLayerConfig?.label;
                return layerLabel && attribute.ciselniky?.some(c => c.nazev === layerLabel);
            });
        });

        return {discriminatorAttribute: discriminatorAttribute ?? undefined, matchingCiselnik: undefined};
    }, [layerAttributesQuery.data, layersConfigQuery.data, selectedLayerId, attributeTableDiscriminatedChildId, userDbLayers, isParentOfUserDbLayersLocal]);
};
